/**
 * Site-copy CMS schemas — the console's mirror of
 * `plustown-be/docs/api/site-copy/admin-*.md`.
 *
 * Two rules shape this file:
 *  1. A copy value is **never** `z.any()`. It is one of three concrete shapes
 *     (`string`, `string[]`, a list of items whose fields are a string or a
 *     list of strings) and the shape is carried by the catalog row, so
 *     `copyValueForShape()` narrows a value the moment we know which key it
 *     belongs to.
 *  2. Every field name matches the wire exactly. The backend is the source of
 *     truth for copy state; the console never derives a field the API omits.
 */

import { z } from 'zod';
import { formatZodIssues, nullableList } from '../zod-helpers';

const uuid = z.string().uuid();

/* -------------------------------------------------------------------------- */
/* Primitives                                                                  */
/* -------------------------------------------------------------------------- */

export const COPY_LOCALES = ['en', 'ar', 'eg-ar'] as const;
export const copyLocaleSchema = z.enum(COPY_LOCALES);
export type CopyLocale = z.infer<typeof copyLocaleSchema>;

export const copyShapeSchema = z.enum(['string', 'string_list', 'object_list']);
export type CopyShape = z.infer<typeof copyShapeSchema>;

export const copyFieldRoleSchema = z.enum(['text', 'structural']);
export type CopyFieldRole = z.infer<typeof copyFieldRoleSchema>;

export const copyItemFieldSchema = z.object({
  name: z.string().min(1),
  role: copyFieldRoleSchema,
});
export type CopyItemField = z.infer<typeof copyItemFieldSchema>;

export const copyEntryStatusSchema = z.enum(['clean', 'draft', 'published']);
export type CopyEntryStatus = z.infer<typeof copyEntryStatusSchema>;

/** Every leaf of a copy value is a plain string — site copy is text, never markup. */
export const copyStringValueSchema = z.string();
export const copyStringListValueSchema = z.array(z.string());

/**
 * One field of an object-list item: a string, or a list of strings
 * (`community.chaptersRegions` items carry `cities: ["cairo", …]`). This is
 * the contract sync enforces (`validateBaselineValue`) and draft saves pin
 * (`sameLeafKind`); a string-only mirror of it took the catalog down on prod.
 */
export const copyItemFieldValueSchema = z.union([z.string(), z.array(z.string())]);
export type CopyItemFieldValue = z.infer<typeof copyItemFieldValueSchema>;
export type CopyItem = Record<string, CopyItemFieldValue>;
export const copyObjectListValueSchema = z.array(z.record(z.string(), copyItemFieldValueSchema));

/**
 * The union used when the shape isn't known yet (a catalog row carries values
 * for keys of every shape). Ordering matters: `z.string()` first so a plain
 * string never gets coerced by an array branch.
 */
export const copyValueSchema = z.union([
  copyStringValueSchema,
  copyStringListValueSchema,
  copyObjectListValueSchema,
]);
export type CopyValue = z.infer<typeof copyValueSchema>;

/** Narrow a value parser to one shape — used on every write path. */
export function copyValueForShape(shape: CopyShape): z.ZodType<CopyValue> {
  switch (shape) {
    case 'string':
      return copyStringValueSchema;
    case 'string_list':
      return copyStringListValueSchema;
    case 'object_list':
      return copyObjectListValueSchema;
  }
}

/** `{en?, ar?, eg-ar?}` map. The API always sends all three; we stay tolerant. */
function localeMap<T extends z.ZodTypeAny>(value: T) {
  return z.object({
    en: value.optional(),
    ar: value.optional(),
    'eg-ar': value.optional(),
  });
}

/* -------------------------------------------------------------------------- */
/* Catalog                                                                     */
/* -------------------------------------------------------------------------- */

export const copyLocaleStateSchema = z.object({
  locale: copyLocaleSchema,
  status: copyEntryStatusSchema,
  version: z.number().int(),
  draft_value: copyValueSchema.nullable(),
  published_value: copyValueSchema.nullable(),
  updated_by: uuid.nullable().optional(),
  updated_at: z.string().nullable().optional(),
  published_at: z.string().nullable().optional(),
});
export type CopyLocaleState = z.infer<typeof copyLocaleStateSchema>;

export const copyKeySchema = z.object({
  key: z.string().min(1),
  namespace: z.string(),
  surface: z.string(),
  shape: copyShapeSchema,
  item_fields: nullableList(copyItemFieldSchema),
  placeholders: nullableList(z.string()),
  max_len: z.number().int().nullable(),
  baseline: localeMap(copyValueSchema),
  baseline_hash: z.string(),
  manifest_version: z.string(),
  orphaned_at: z.string().nullable(),
  orphaned: z.boolean(),
  baseline_changed_at: z.string().nullable(),
  baseline_changed: z.boolean(),
  needs_eg_ar: z.boolean(),
  locales: localeMap(copyLocaleStateSchema),
});
export type CopyKey = z.infer<typeof copyKeySchema>;

/** A catalog row the console could not parse, kept so the screen can name it. */
export interface CopyInvalidRow {
  /** The row's `key` when it has a readable one — `null` when even that is broken. */
  key: string | null;
  /** First issues only, as `path: message` — enough to file, short enough to show. */
  issues: string[];
}

const MAX_ROW_ISSUES = 5;

/**
 * One catalog page. Each row is validated on its own against `copyKeySchema`:
 * a single row that breaks the contract is reported in `invalid` and the rest
 * of the page still renders, instead of one bad key blanking all 624 (the
 * 2026-09-13 outage). The envelope itself stays strict.
 */
export const copyCatalogResponseSchema = z
  .object({
    items: nullableList(z.unknown()),
    next_cursor: z.string().default(''),
  })
  .transform(({ items, next_cursor }) => {
    const valid: CopyKey[] = [];
    const invalid: CopyInvalidRow[] = [];
    for (const raw of items) {
      const parsed = copyKeySchema.safeParse(raw);
      if (parsed.success) {
        valid.push(parsed.data);
        continue;
      }
      const key =
        raw !== null &&
        typeof raw === 'object' &&
        typeof (raw as { key?: unknown }).key === 'string'
          ? (raw as { key: string }).key
          : null;
      invalid.push({ key, issues: formatZodIssues(parsed.error, MAX_ROW_ISSUES) });
    }
    return { items: valid, invalid, next_cursor };
  });
export type CopyCatalogResponse = z.output<typeof copyCatalogResponseSchema>;

export const copyStatusFilterSchema = z.enum(['clean', 'draft', 'published']);
export type CopyStatusFilter = z.infer<typeof copyStatusFilterSchema>;

export const copyFlagFilterSchema = z.enum([
  'needs_eg_ar',
  'orphaned',
  'baseline_changed',
  'overridden',
  'pending',
]);
export type CopyFlagFilter = z.infer<typeof copyFlagFilterSchema>;

/**
 * The rows-per-page choices and the default. The default is the backend's own
 * `httpx.ParsePage` default, so the select, the URL (which omits the default),
 * the query key and a bare API call all mean the same page.
 */
export const COPY_CATALOG_PAGE_SIZES = [25, 50, 100] as const;
export type CopyCatalogPageSize = (typeof COPY_CATALOG_PAGE_SIZES)[number];
export const DEFAULT_COPY_CATALOG_PAGE_SIZE: CopyCatalogPageSize = 25;

export const copyCatalogFiltersSchema = z.object({
  q: z.string().max(200).optional(),
  namespace: z.string().max(80).optional(),
  surface: z.string().max(80).optional(),
  status: copyStatusFilterSchema.optional(),
  flag: copyFlagFilterSchema.optional(),
  limit: z.number().int().min(1).max(100).optional(),
  cursor: z.string().optional(),
});
export type CopyCatalogFilters = z.infer<typeof copyCatalogFiltersSchema>;

/* -------------------------------------------------------------------------- */
/* One key: detail + revisions                                                 */
/* -------------------------------------------------------------------------- */

export const copyRevisionActionSchema = z.enum([
  'draft_saved',
  'published',
  'reverted',
  'override_cleared',
  'synced',
]);
export type CopyRevisionAction = z.infer<typeof copyRevisionActionSchema>;

export const copyRevisionSchema = z.object({
  id: z.number().int(),
  locale: copyLocaleSchema,
  value: copyValueSchema.nullable(),
  action: copyRevisionActionSchema,
  actor_id: uuid.nullable(),
  bundle_version: z.number().int().nullable(),
  created_at: z.string(),
});
export type CopyRevision = z.infer<typeof copyRevisionSchema>;

export const copyKeyDetailSchema = z.object({
  key: copyKeySchema,
  revisions: nullableList(copyRevisionSchema),
});
export type CopyKeyDetail = z.infer<typeof copyKeyDetailSchema>;

/* -------------------------------------------------------------------------- */
/* Writes                                                                      */
/* -------------------------------------------------------------------------- */

/**
 * `base_version` is required and never defaulted — the backend rejects an
 * omitted one precisely because a client defaulting to 0 would clobber a
 * concurrent edit (admin-keys.md).
 */
export const copyDraftUpsertSchema = z.object({
  value: copyValueSchema,
  base_version: z.number().int().min(0),
});
export type CopyDraftUpsert = z.infer<typeof copyDraftUpsertSchema>;

export const copyWarningSchema = z.object({
  code: z.string(),
  field: z.string().default(''),
  message: z.string(),
});
export type CopyWarning = z.infer<typeof copyWarningSchema>;

export const copyDraftSaveResponseSchema = z.object({
  key: z.string(),
  locale: copyLocaleSchema,
  status: copyEntryStatusSchema,
  version: z.number().int(),
  draft_value: copyValueSchema.nullable(),
  warnings: nullableList(copyWarningSchema),
});
export type CopyDraftSaveResponse = z.infer<typeof copyDraftSaveResponseSchema>;

/** A no-op clear omits `version` — clearing an already-clean key bumps nothing. */
export const copyClearResponseSchema = z.object({
  key: z.string(),
  locale: copyLocaleSchema,
  status: copyEntryStatusSchema,
  version: z.number().int().optional(),
  cleared: z.boolean(),
});
export type CopyClearResponse = z.infer<typeof copyClearResponseSchema>;

/* -------------------------------------------------------------------------- */
/* Pending + publish                                                           */
/* -------------------------------------------------------------------------- */

export const copyPendingDraftSchema = z.object({
  key: z.string(),
  locale: copyLocaleSchema,
  shape: copyShapeSchema,
  surface: z.string(),
  baseline: copyValueSchema.nullable(),
  published_value: copyValueSchema.nullable(),
  draft_value: copyValueSchema.nullable(),
  version: z.number().int(),
  updated_by: uuid.nullable().optional(),
  updated_at: z.string().optional(),
});
export type CopyPendingDraft = z.infer<typeof copyPendingDraftSchema>;

export const copyPendingRemovalSchema = z.object({
  key: z.string(),
  locale: copyLocaleSchema,
  served_value: copyValueSchema.nullable(),
});
export type CopyPendingRemoval = z.infer<typeof copyPendingRemovalSchema>;

export const copyPendingResponseSchema = z.object({
  drafts: nullableList(copyPendingDraftSchema),
  removals: nullableList(copyPendingRemovalSchema),
  count: z.number().int(),
});
export type CopyPendingResponse = z.infer<typeof copyPendingResponseSchema>;

export const copyPublishRequestSchema = z.object({
  keys: z.array(z.string().max(200)).max(1000).optional(),
  note: z.string().max(500).optional(),
});
export type CopyPublishRequest = z.infer<typeof copyPublishRequestSchema>;

export const copyPublishResponseSchema = z.object({
  version: z.number().int(),
  published: z.boolean(),
  published_at: z.string().optional(),
  changed_keys: nullableList(z.string()),
  promoted: z.number().int().optional(),
});
export type CopyPublishResponse = z.infer<typeof copyPublishResponseSchema>;

/* -------------------------------------------------------------------------- */
/* Catalog sync                                                                */
/* -------------------------------------------------------------------------- */

/**
 * `POST /admin/v1/site-copy/sync`. The console sends `{}` so the API reads its
 * own configured copy site; `manifest_url` stays out of the UI on purpose — a
 * cross-environment sync is an ops action, not an editorial one.
 */
export const copySyncRequestSchema = z.object({
  dry_run: z.boolean().optional(),
});
export type CopySyncRequest = z.infer<typeof copySyncRequestSchema>;

export const copySyncReportSchema = z.object({
  manifest_version: z.string(),
  generated_at: z.string(),
  source: z.string(),
  keys: z.number().int(),
  added: z.number().int(),
  updated: z.number().int(),
  unchanged: z.number().int(),
  orphaned: z.number().int(),
  unorphaned: z.number().int(),
  orphaned_keys: nullableList(z.string()),
  unorphaned_keys: nullableList(z.string()),
  baseline_changed: nullableList(z.string()),
  dry_run: z.boolean(),
  forced: z.boolean(),
});
export type CopySyncReport = z.infer<typeof copySyncReportSchema>;

/* -------------------------------------------------------------------------- */
/* Bundles                                                                     */
/* -------------------------------------------------------------------------- */

export const copyBundleSourceSchema = z.enum(['publish', 'revert']);
export type CopyBundleSource = z.infer<typeof copyBundleSourceSchema>;

export const copyBundleSchema = z.object({
  version: z.number().int(),
  changed_keys: nullableList(z.string()),
  note: z.string().nullable(),
  published_by: uuid.nullable(),
  published_at: z.string(),
  source: copyBundleSourceSchema,
  snapshot_bytes: z.number().int().optional(),
});
export type CopyBundle = z.infer<typeof copyBundleSchema>;

export const copyBundleListResponseSchema = z.object({
  items: nullableList(copyBundleSchema),
  next_cursor: z.string().default(''),
});
export type CopyBundleListResponse = z.infer<typeof copyBundleListResponseSchema>;

export const copyBundleDetailSchema = copyBundleSchema.extend({
  snapshot: localeMap(z.record(z.string(), copyValueSchema)),
});
export type CopyBundleDetail = z.infer<typeof copyBundleDetailSchema>;

export const copyBundleRevertRequestSchema = z.object({
  note: z.string().max(500).optional(),
});
export type CopyBundleRevertRequest = z.infer<typeof copyBundleRevertRequestSchema>;

export const copyBundleRevertResponseSchema = z.object({
  version: z.number().int(),
  reverted_to: z.number().int(),
  published_at: z.string().optional(),
  changed_keys: nullableList(z.string()),
});
export type CopyBundleRevertResponse = z.infer<typeof copyBundleRevertResponseSchema>;

/* -------------------------------------------------------------------------- */
/* Helpers shared by the UI                                                    */
/* -------------------------------------------------------------------------- */

/** The value an editor starts from: draft → published → shipped baseline. */
export function effectiveValue(
  key: CopyKey,
  locale: CopyLocale
): { value: CopyValue | null; origin: 'draft' | 'published' | 'baseline' } {
  const state = key.locales[locale];
  if (state?.draft_value != null) return { value: state.draft_value, origin: 'draft' };
  if (state?.published_value != null) return { value: state.published_value, origin: 'published' };
  return { value: key.baseline[locale] ?? null, origin: 'baseline' };
}

/** The `base_version` to send for a locale — 0 when no entry row exists yet. */
export function baseVersionFor(key: CopyKey, locale: CopyLocale): number {
  return key.locales[locale]?.version ?? 0;
}

/** The status chip a row shows for one locale. */
export function localeStatus(key: CopyKey, locale: CopyLocale): CopyEntryStatus {
  return key.locales[locale]?.status ?? 'clean';
}
